import { useState, useEffect, useRef, useMemo } from "react";
import { Form, InputGroup } from "react-bootstrap";
import SimpleBar from "simplebar-react";
import {projections} from "../../utilities/sridData";
import stringUtilities from "../../utilities/stringUtilities";
import arrayUtilities from "../../utilities/arrayUtilities";

// Returns a new function that delays invoking callback until delay ms after the last call.
function debounce(callback, delay) {
    let timeout;
    return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => callback.apply(this, args), delay);
    };
}

// Recursively walks a section/group node to find the display name for a given EPSG code.
function searchInContainer(container, epsg) {
    if (container.srids) {
        const match = container.srids.find(srid => srid.epsg === epsg);
        if (match) {
            return match.name;
        }
    }
    if (container.groups) {
        for (const group of Object.values(container.groups)) {
            const matchedName = searchInContainer(group, epsg);
            if (matchedName) {
                return matchedName;
            }
        }
    }
    return null;
}

// Recursively prunes a section/group node to only entries matching the search term.
// Mutates openedMap to mark any group that contains a match as auto-opened.
// Returns null if no matches exist within this node.
function filterContainer(container, searchTerm, openedMap) {
    if (container.srids) {
        const matchingSrids = container.srids.filter(srid =>
            srid.name.toLowerCase().includes(searchTerm) || String(srid.epsg).includes(searchTerm)
        );
        return matchingSrids.length > 0 ? { ...container, srids: matchingSrids } : null;
    }
    if (container.groups) {
        const matchingGroups = {};
        for (const [key, group] of Object.entries(container.groups)) {
            const filteredGroup = filterContainer(group, searchTerm, openedMap);
            if (filteredGroup) {
                matchingGroups[key] = filteredGroup;
                openedMap[key] = true;
            }
        }
        return Object.keys(matchingGroups).length > 0
            ? { ...container, groups: matchingGroups }
            : null;
    }
    return null;
}

export default function SridSelector({value, onChange}) {
    const [filter, setFilter] = useState("");
    const [filteredData, setFilteredData] = useState(projections);
    // opened is a map of group/section keys to boolean — absent key means collapsed.
    const [opened, setOpened] = useState({});
    const [filterFocused, setFilterFocused] = useState(false);
    // Stored in a ref so the second useEffect always calls the same debounced instance.
    const debouncedRef = useRef(null);

    const toggle = (key) => setOpened(prev => (
        { 
            ...prev, 
            [key]: !prev[key] 
        }
    ));

    const onSelect = (srid) => {
        if (onChange) {
            // onChange(srid.epsg);
            onChange(srid);
        }
        setFilter("");
    };

    // Create the debounced filter function once on mount.
    useEffect(() => {
        debouncedRef.current = debounce((searchText) => {
            if (stringUtilities.isNullOrEmpty(searchText)) {
                setFilteredData(projections);
            } else {
                const searchTerm = searchText.toLowerCase();
                const openedMap = {};
                const matchedSections = {};
                for (const [key, section] of Object.entries(projections)) {
                    const filteredSection = filterContainer(section, searchTerm, openedMap);
                    if (filteredSection) {
                        matchedSections[key] = filteredSection;
                        // Also auto-open the top-level section if it has matches.
                        openedMap[`section-${key}`] = true;
                    }
                }
                setFilteredData(matchedSections);
                setOpened(openedMap);
            }
        }, 300);
    }, []);

    useEffect(() => {
        if (debouncedRef.current) {
            debouncedRef.current(filter);
        }
    }, [filter]);

    // Resolve the display name for the currently selected EPSG by walking the tree.
    // Memoized on value — avoids re-walking the full dataset on unrelated renders (filter typing, focus, folder toggles).
    const selectedName = useMemo(() => {
        if (value === null || value === undefined) return null;
        for (const section of Object.values(projections)) {
            const matchedName = searchInContainer(section, value);
            if (matchedName) {
                return matchedName;
            }
        }
        return null;
    }, [value]);

    const selectedLabel = selectedName ? `${selectedName} (EPSG: ${value})` : null;
    // Show the selected label in the input when nothing is being typed and the input isn't focused.
    // On focus the input clears to let the user type without the label interfering.
    const showingSelection = !!selectedLabel && !filter && !filterFocused;
    const filterInputValue = showingSelection ? selectedLabel : filter;

    const renderSrid = (srid) => (
        <li
            key={srid.epsg}
            className={`d-flex align-items-center srid-leaf${value === srid.epsg ? ' active' : ''}`}
            onClick={() => onSelect(srid)}
            role="button"
        >
            <span>{srid.name}</span>
            <span className="text-muted ms-1 fs-xs">(EPSG: {srid.epsg})</span>
        </li>
    );

    // Renders a collapsible group node; recurses for nested groups.
    const renderGroup = (groupKey, group) => {
        const isOpen = !!opened[groupKey];
        return (
            <ul key={groupKey} className={`ul-container${isOpen ? "" : " collapsed"}`}>
                <li>
                    <a className="d-flex align-items-center" onClick={(e) => { e.stopPropagation(); toggle(groupKey); }}>
                        <i className={isOpen ? 'fi-folder-minus' : 'fi-folder-plus'}></i>
                        {group.label}
                    </a>
                    {!arrayUtilities.isNullOrEmpty(group.srids) && (
                        <ul>
                            {group.srids.map(renderSrid)}
                        </ul>
                    )}
                    {
                        group.groups && 
                        Object.entries(group.groups).map(([subKey, subGroup]) => renderGroup(subKey, subGroup))
                    }
                </li>
            </ul>
        );
    };

    // Renders a top-level section; uses a "section-N" key prefix to avoid colliding with group keys.
    const renderSection = ([key, section]) => {
        const sectionKey = `section-${key}`;
        const isOpen = !!opened[sectionKey];
        return (
            <ul key={key} className={`ul-container${isOpen ? "" : " collapsed"}`}>
                <li>
                    <a className="d-flex align-items-center" onClick={(e) => { e.stopPropagation(); toggle(sectionKey); }}>
                        <i className={isOpen ? 'fi-folder-minus' : 'fi-folder-plus'}></i>
                        {section.label}
                    </a>
                    {!arrayUtilities.isNullOrEmpty(section.srids) && (
                        <ul>
                            {section.srids.map(renderSrid)}
                        </ul>
                    )}
                    {
                        section.groups 
                        && Object.entries(section.groups).map(([subKey, subGroup]) => renderGroup(subKey, subGroup))
                    }
                </li>
            </ul>
        );
    };

    const hasResults = Object.keys(filteredData).length > 0;

    return (
        <Form.Group className="mb-1 d-flex flex-column h-100">
            <Form.Group className="d-flex align-items-center flex-shrink-0 mb-2">
                <Form.Label className="text-body fs-sm me-2 mb-0 pe-1 text-nowrap">
                    <i className="fi-filter text-muted mt-n1"></i>
                </Form.Label>
                <InputGroup size="sm">
                    <Form.Control
                        size="sm"
                        placeholder="Search SRID"
                        value={filterInputValue}
                        onChange={(e) => setFilter(e.target.value)}
                        onFocus={() => setFilterFocused(true)}
                        onBlur={() => setFilterFocused(false)}
                    />
                    <InputGroup.Text className="p-0">
                        <a href="#" className="d-block text-body px-3 py-2" onClick={() => setFilter("")}>
                            <i className="fi-x mt-n1"></i>
                        </a>
                    </InputGroup.Text>
                </InputGroup>
            </Form.Group>
            <SimpleBar autoHide={false} className="simplebar-no-autohide pe-3 mb-3" style={{ flex: 1, minHeight: 0 }}>
                {hasResults
                    ? Object.entries(filteredData).map(renderSection)
                    : <p className="text-muted fs-xs py-2 text-center mb-0">No results</p>
                }
            </SimpleBar>
        </Form.Group>
    );
}
